import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAnalytics } from "../../lib/analytics";
import * as documentService from "../../services/documentService";
import type { Document, DocumentCategory } from "../../types/document";
import { Dropdown } from "../ui/form";
import { DocumentDetailModal } from "./DocumentDetailModal";
import { DocumentGrid } from "./DocumentGrid";
import { DocumentList } from "./DocumentList";
import styles from "./DocumentsContainer.module.css";
import { DocumentUploader } from "./DocumentUploader";

type ViewMode = "grid" | "list";

export function DocumentsContainer() {
	const { t } = useTranslation("documents");
	const { logEvent } = useAnalytics();
	const [documents, setDocuments] = useState<Document[]>([]);
	const [loading, setLoading] = useState(true);
	const [viewMode, setViewMode] = useState<ViewMode>("grid");
	const [searchQuery, setSearchQuery] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [selectedCategory, setSelectedCategory] = useState<
		DocumentCategory | "all"
	>("all");
	const [sharedOnly, setSharedOnly] = useState(false);
	const [selectedDocument, setSelectedDocument] = useState<Document | null>(
		null,
	);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isUploading, setIsUploading] = useState(false);

	// Pagination state
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [_totalDocuments, setTotalDocuments] = useState(0);

	// Debounce search query
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(searchQuery);
			if (searchQuery.trim()) {
				logEvent("documents_search", undefined, {
					query_length: searchQuery.length,
				});
			}
		}, 500);

		return () => clearTimeout(timer);
	}, [searchQuery, logEvent]);

	// Fetch documents when filters change
	useEffect(() => {
		let isMounted = true;

		const fetchDocuments = async () => {
			setLoading(true);
			try {
				const response = await documentService.getAllDocuments({
					search: debouncedSearch || undefined,
					category: selectedCategory === "all" ? undefined : selectedCategory,
					sharedOnly,
					page: currentPage,
					limit: 20,
				});

				if (isMounted) {
					setDocuments(response.documents);
					setTotalPages(response.pagination.totalPages);
					setTotalDocuments(response.pagination.total);

					logEvent("documents_fetched", undefined, {
						page: currentPage,
						category: selectedCategory,
						sharedOnly,
					});
				}
			} catch (error) {
				if (isMounted) {
					console.error("Error fetching documents:", error);
					toast.error(t("errors.fetchFailed"));
				}
			} finally {
				if (isMounted) {
					setLoading(false);
				}
			}
		};

		fetchDocuments();

		return () => {
			isMounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearch, selectedCategory, sharedOnly, currentPage, logEvent, t]);

	const handleUploadComplete = async (uploadedDocument: Document) => {
		setDocuments((prev) => [uploadedDocument, ...prev]);
		setTotalDocuments((prev) => prev + 1);
		toast.success(t("upload.success"));

		logEvent("document_uploaded", undefined, {
			documentId: uploadedDocument.id,
			category: uploadedDocument.category,
			mimeType: uploadedDocument.mimeType,
		});
	};

	const handleDocumentClick = (document: Document) => {
		setSelectedDocument(document);
		setIsModalOpen(true);

		logEvent("document_viewed", undefined, {
			documentId: document.id,
			category: document.category,
		});
	};

	const handleDocumentDelete = async (documentId: string) => {
		try {
			await documentService.deleteDocument(documentId);

			setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
			setTotalDocuments((prev) => prev - 1);
			setIsModalOpen(false);
			setSelectedDocument(null);
			toast.success(t("delete.success"));

			logEvent("document_deleted", undefined, { documentId });
		} catch (error) {
			console.error("Error deleting document:", error);
			toast.error(t("errors.deleteFailed"));
		}
	};

	const handleCategoryChange = async (
		documentId: string,
		newCategory: DocumentCategory,
	) => {
		try {
			await documentService.updateDocumentCategory(documentId, newCategory);

			setDocuments((prev) =>
				prev.map((doc) =>
					doc.id === documentId ? { ...doc, category: newCategory } : doc,
				),
			);

			if (selectedDocument?.id === documentId) {
				setSelectedDocument({ ...selectedDocument, category: newCategory });
			}

			toast.success(t("category.updateSuccess"));

			logEvent("document_category_updated", undefined, {
				documentId,
				newCategory,
			});
		} catch (error) {
			console.error("Error updating category:", error);
			toast.error(t("errors.updateFailed"));
		}
	};

	const categories: Array<{ value: DocumentCategory | "all"; label: string }> =
		[
			{ value: "all", label: t("categories.all") },
			{ value: "lab_results", label: t("categories.lab_results") },
			{
				value: "prescriptions",
				label: t("categories.prescriptions"),
			},
			{ value: "imaging", label: t("categories.imaging") },
			{
				value: "medical_records",
				label: t("categories.medical_records"),
			},
			{ value: "insurance", label: t("categories.insurance") },
			{ value: "other", label: t("categories.other") },
		];

	return (
		<div className={styles.container}>
			{/* Header with filters */}
			<div className={styles.header}>
				<div className={styles.titleRow}>
					<h1 className={styles.title}>{t("title")}</h1>
					<div className={styles.viewToggle}>
						<button
							type="button"
							className={`${styles.viewButton} ${viewMode === "grid" ? styles.active : ""}`}
							onClick={() => {
								setViewMode("grid");
								logEvent("documents_view_mode_changed", undefined, {
									mode: "grid",
								});
							}}
							aria-label={t("view.grid")}
						>
							<i className="pi pi-th-large" />
						</button>
						<button
							type="button"
							className={`${styles.viewButton} ${viewMode === "list" ? styles.active : ""}`}
							onClick={() => {
								setViewMode("list");
								logEvent("documents_view_mode_changed", undefined, {
									mode: "list",
								});
							}}
							aria-label={t("view.list")}
						>
							<i className="pi pi-list" />
						</button>
					</div>
				</div>

				<div className={styles.filters}>
					<div className={styles.searchBox}>
						<i className="pi pi-search" />
						<input
							type="text"
							placeholder={t("search.placeholder")}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className={styles.searchInput}
						/>
					</div>

					<Dropdown
						options={categories}
						value={selectedCategory}
						onChange={(value) => {
							setSelectedCategory(value as DocumentCategory | "all");
							setCurrentPage(1);
							logEvent("documents_category_filter_changed", undefined, {
								category: value,
							});
						}}
						placeholder={t("categories.all")}
						className={styles.categoryDropdown}
					/>

					<label className={styles.checkbox}>
						<input
							type="checkbox"
							checked={sharedOnly}
							onChange={(e) => {
								setSharedOnly(e.target.checked);
								setCurrentPage(1);
								logEvent("documents_shared_only_toggled", undefined, {
									enabled: e.target.checked,
								});
							}}
						/>
						<span>{t("filters.sharedOnly")}</span>
					</label>
				</div>
			</div>

			{/* Uploader */}
			<DocumentUploader
				onUploadComplete={handleUploadComplete}
				isUploading={isUploading}
				setIsUploading={setIsUploading}
			/>

			{/* Documents display */}
			{loading ? (
				<div className={styles.loading}>
					<i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
					<p>{t("loading")}</p>
				</div>
			) : documents.length === 0 ? (
				<div className={styles.empty}>
					<h3>{t("empty.title")}</h3>
					<p>{t("empty.description")}</p>
				</div>
			) : viewMode === "grid" ? (
				<DocumentGrid
					documents={documents}
					onDocumentClick={handleDocumentClick}
				/>
			) : (
				<DocumentList
					documents={documents}
					onDocumentClick={handleDocumentClick}
				/>
			)}

			{/* Pagination */}
			{totalPages > 1 && (
				<div className={styles.pagination}>
					<button
						type="button"
						disabled={currentPage === 1}
						onClick={() => setCurrentPage((p) => p - 1)}
						className={styles.paginationButton}
					>
						<i className="pi pi-chevron-left" />
						{t("pagination.previous")}
					</button>

					<span className={styles.pageInfo}>
						{t("pagination.info", {
							current: currentPage,
							total: totalPages,
						})}
					</span>

					<button
						type="button"
						disabled={currentPage === totalPages}
						onClick={() => setCurrentPage((p) => p + 1)}
						className={styles.paginationButton}
					>
						{t("pagination.next")}
						<i className="pi pi-chevron-right" />
					</button>
				</div>
			)}

			{/* Detail Modal */}
			{selectedDocument && (
				<DocumentDetailModal
					document={selectedDocument}
					isOpen={isModalOpen}
					onClose={() => {
						setIsModalOpen(false);
						setSelectedDocument(null);
					}}
					onDelete={handleDocumentDelete}
					onCategoryChange={handleCategoryChange}
				/>
			)}
		</div>
	);
}
